import React, { useMemo, useState } from 'react';
import {
  ActionDrawerItems,
  ActionMenu,
  Avatar,
  DrawerList,
  Empty,
  Icon,
  IconButton,
  Input,
  Modal,
  PageHeading,
  Pills,
  Select,
  Table,
  Text,
  Tooltip,
  useToast,
} from 'barte-design-system';
import type { ColumnDefinition } from 'barte-design-system';
import {
  ArrowClockwise,
  DotsThree,
  Envelope,
  Eye,
  Key,
  MagnifyingGlass,
  PencilSimple,
  Prohibit,
} from '@phosphor-icons/react';
import { PortalPayfacsLayout } from '../PortalPayfacsLayout';
import { DrawerUsuario, type DrawerMode, type StatusAction } from './DrawerUsuario';
import { ModalMatrizRoles } from './ModalMatrizRoles';
import { MOCK_USERS, ROLES, ROLE_BY_ID, ROLE_ICON, type User, type UserStatus } from './roles';
import './UsuariosPermissoes.css';

// ─── Status → Pills state ────────────────────────────────────────────────────────

const STATUS_PILL_STATE: Record<UserStatus, 'success' | 'warning' | 'error' | 'default'> = {
  Ativo: 'success',
  Pendente: 'warning',
  Expirado: 'error',
  Inativo: 'default',
};

const STATUS_OPTIONS = [
  { label: 'Todos os status', value: 'todos' },
  { label: 'Ativo', value: 'Ativo' },
  { label: 'Pendente', value: 'Pendente' },
  { label: 'Expirado', value: 'Expirado' },
  { label: 'Inativo', value: 'Inativo' },
];

const ROLE_OPTIONS = [
  { label: 'Todos os perfis', value: 'todos' },
  ...ROLES.map((r) => ({ label: r.name, value: r.id })),
];

const PAGE_SIZE = 8;

type ConfirmKind = 'inativar' | null;

export function UsuariosPermissoes() {
  const toast = useToast();

  const [users, setUsers] = useState<User[]>(MOCK_USERS);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [roleFilter, setRoleFilter] = useState('todos');
  const [page, setPage] = useState(1);

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>('view');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Modais
  const [matrizOpen, setMatrizOpen] = useState(false);
  const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
  const [confirmUser, setConfirmUser] = useState<User | null>(null);

  // ─── Filtragem ───────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      const matchQ = !q || u.email.toLowerCase().includes(q);
      const matchStatus = statusFilter === 'todos' || u.status === statusFilter;
      const matchRole = roleFilter === 'todos' || u.roleIds.includes(roleFilter);
      return matchQ && matchStatus && matchRole;
    });
  }, [users, search, statusFilter, roleFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagedData = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  function resetPage() {
    setPage(1);
  }

  // ─── Ações ─────────────────────────────────────────────────────────────────────
  function openCreate() {
    setSelectedUser(null);
    setDrawerMode('create');
    setDrawerOpen(true);
  }

  function openView(user: User) {
    setSelectedUser(user);
    setDrawerMode('view');
    setDrawerOpen(true);
  }

  function openEdit(user: User) {
    setSelectedUser(user);
    setDrawerMode('edit');
    setDrawerOpen(true);
  }

  function handleCreate(data: { email: string; roleIds: string[] }) {
    const newUser: User = {
      id: `USR-${String(users.length + 1).padStart(3, '0')}`,
      nome: '',
      email: data.email,
      status: 'Pendente',
      roleIds: data.roleIds,
      lastAccess: '—',
      companies: [],
    };
    setUsers((prev) => [newUser, ...prev]);
    setDrawerOpen(false);
    resetPage();
    toast.success({ title: 'Convite enviado', description: `Um convite foi enviado para ${data.email}.` });
  }

  function handleSave(userId: string, data: { email: string; roleIds: string[] }) {
    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId ? { ...u, email: data.email, roleIds: data.roleIds } : u,
      ),
    );
    setSelectedUser((prev) => (prev && prev.id === userId ? { ...prev, ...data } : prev));
    toast.success({ title: 'Alterações salvas', description: 'Os dados do usuário foram atualizados.' });
  }

  function handleResendInvite(user: User) {
    toast.success({ title: 'Convite reenviado', description: `Novo convite enviado para ${user.email}.` });
  }

  function handleResetPassword(user: User) {
    toast.success({ title: 'Redefinir senha', description: `${user.email} receberá um link para redefinir a senha.` });
  }

  function handleReactivate(user: User) {
    setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, status: 'Ativo' } : u)));
    setSelectedUser((prev) => (prev && prev.id === user.id ? { ...prev, status: 'Ativo' } : prev));
    toast.success({ title: 'Usuário reativado', description: `${user.email} voltou a ter acesso ao portal.` });
  }

  // Ações disparadas pela pill de status dentro do drawer
  function handleStatusAction(action: StatusAction, user: User) {
    if (action === 'inativar') {
      setDrawerOpen(false);
      openConfirm('inativar', user);
    } else if (action === 'reativar') {
      handleReactivate(user);
    } else {
      handleResendInvite(user);
    }
  }

  function openConfirm(kind: Exclude<ConfirmKind, null>, user: User) {
    setConfirmKind(kind);
    setConfirmUser(user);
  }

  function closeConfirm() {
    setConfirmKind(null);
    setConfirmUser(null);
  }

  function handleConfirm() {
    if (!confirmUser || !confirmKind) return;
    setUsers((prev) => prev.map((u) => (u.id === confirmUser.id ? { ...u, status: 'Inativo' } : u)));
    setSelectedUser((prev) => (prev && prev.id === confirmUser.id ? { ...prev, status: 'Inativo' } : prev));
    toast.success({ title: 'Usuário desativado', description: `${confirmUser.email} não tem mais acesso ao portal.` });
    closeConfirm();
  }

  // ─── Menu de ações condicional por status ──────────────────────────────────────
  function buildActionItems(user: User) {
    const items: {
      label: string;
      variant: 'icon';
      icon: React.ReactElement;
      onClick: () => void;
    }[] = [
      { label: 'Ver detalhes', variant: 'icon', icon: <Icon icon={Eye} size={20} weight="regular" />, onClick: () => openView(user) },
      { label: 'Editar', variant: 'icon', icon: <Icon icon={PencilSimple} size={20} weight="regular" />, onClick: () => openEdit(user) },
    ];

    if (user.status === 'Pendente' || user.status === 'Expirado') {
      items.push({ label: 'Reenviar convite', variant: 'icon', icon: <Icon icon={ArrowClockwise} size={20} weight="regular" />, onClick: () => handleResendInvite(user) });
    }
    if (user.status === 'Ativo') {
      items.push({ label: 'Redefinir senha', variant: 'icon', icon: <Icon icon={Key} size={20} weight="regular" />, onClick: () => handleResetPassword(user) });
      items.push({ label: 'Desativar', variant: 'icon', icon: <Icon icon={Prohibit} size={20} weight="regular" />, onClick: () => openConfirm('inativar', user) });
    }
    if (user.status === 'Inativo') {
      items.push({ label: 'Reativar', variant: 'icon', icon: <Icon icon={ArrowClockwise} size={20} weight="regular" />, onClick: () => handleReactivate(user) });
    }

    return items;
  }

  // ─── Colunas ─────────────────────────────────────────────────────────────────
  const columns = useMemo<ColumnDefinition<User>[]>(
    () => [
      {
        key: 'email',
        title: 'Email',
        dataIndex: 'email',
        sortable: true,
        width: 300,
        render: (_value, record) => (
          <div className="up-email-cell">
            {record.status === 'Pendente' ? (
              <span className="up-avatar-pending" aria-label="Convite pendente">
                <Icon icon={Envelope} size={16} weight="regular" />
              </span>
            ) : (
              <Avatar
                variant="text"
                size="sm"
                initials={record.email.charAt(0).toUpperCase()}
                className="up-avatar"
              />
            )}
            <Text variant="body-sm">{record.email}</Text>
          </div>
        ),
      },
      { key: 'lastAccess', title: 'Último acesso', dataIndex: 'lastAccess', sortable: true, width: 160 },
      {
        key: 'status',
        title: 'Status',
        dataIndex: 'status',
        sortable: true,
        width: 130,
        render: (value) => (
          <span className={`up-status up-status--${(value as string).toLowerCase()}`}>
            <Pills
              label={value as string}
              state={STATUS_PILL_STATE[value as UserStatus]}
              variant={value === 'Inativo' ? 'light' : 'fill'}
              size="sm"
            />
          </span>
        ),
      },
      {
        key: 'roles',
        title: 'Perfis',
        dataIndex: 'roleIds',
        width: 260,
        render: (_value, record) => {
          const roles = record.roleIds.map((id) => ROLE_BY_ID[id]).filter(Boolean);

          const rolePill = (role: (typeof roles)[number]) => (
            <Tooltip
              key={role.id}
              placement="top"
              title={role.name}
              label={
                role.id === 'admin' ? (
                  role.description
                ) : (
                  <ul className="up-perm-tooltip">
                    {role.permissions.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                )
              }
            >
              <span className="up-pill-trigger">
                <Pills
                  label={role.name}
                  state="accent"
                  variant={role.id === 'admin' ? 'fill' : 'light'}
                  size="sm"
                  icon={ROLE_ICON[role.id]}
                />
              </span>
            </Tooltip>
          );

          // Admin = acesso total → mostra apenas a pill "Admin"
          if (roles.some((r) => r.id === 'admin')) {
            const admin = roles.find((r) => r.id === 'admin')!;
            return <div className="up-roles-cell">{rolePill(admin)}</div>;
          }

          const visible = roles.slice(0, 2);
          const hidden = roles.slice(2);
          return (
            <div className="up-roles-cell">
              {visible.map((role) => rolePill(role))}
              {hidden.length > 0 && (
                <Tooltip
                  placement="top"
                  label={
                    <ul className="up-perm-tooltip">
                      {hidden.map((r) => (
                        <li key={r.id}>{r.name}</li>
                      ))}
                    </ul>
                  }
                >
                  <span className="up-pill-trigger">
                    <Pills label={`+${hidden.length}`} state="accent" variant="light" size="sm" />
                  </span>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        key: 'actions',
        title: '',
        dataIndex: 'id',
        width: 56,
        render: (_value, record) => (
          <div className="up-actions-cell" onClick={(e) => e.stopPropagation()}>
            <ActionMenu
              align="right"
              trigger={
                <IconButton variant="ghost" size="sm" aria-label="Ações">
                  <Icon icon={DotsThree} size={20} weight="bold" />
                </IconButton>
              }
            >
              <DrawerList>
                <ActionDrawerItems title="Ações" items={buildActionItems(record)} />
              </DrawerList>
            </ActionMenu>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const hasFilters = search.trim() !== '' || statusFilter !== 'todos' || roleFilter !== 'todos';

  const confirmCopy = {
    title: 'Desativar usuário',
    description: `${confirmUser?.email} perderá o acesso ao portal, mas o histórico será mantido. Você pode reativá-lo depois.`,
    primary: 'Desativar',
  };

  return (
    <>
      <PortalPayfacsLayout activeItem="usuarios-permissoes">
        <main className="content">
          <div className="content-inner">
            <PageHeading
              title="Usuários e permissões"
              secondaryActionLabel="Ver Perfis"
              onSecondaryAction={() => setMatrizOpen(true)}
              primaryActionLabel="Convidar usuário"
              onPrimaryAction={openCreate}
            />

            <div className="up-filter-bar">
              <div className="up-search">
                <Input
                  placeholder="Buscar por email"
                  value={search}
                  icon={MagnifyingGlass}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    setSearch(e.target.value);
                    resetPage();
                  }}
                />
              </div>
              <div className="up-select">
                <Select
                  options={STATUS_OPTIONS}
                  value={statusFilter}
                  onChange={(v) => {
                    setStatusFilter(v);
                    resetPage();
                  }}
                />
              </div>
              <div className="up-select">
                <Select
                  options={ROLE_OPTIONS}
                  value={roleFilter}
                  onChange={(v) => {
                    setRoleFilter(v);
                    resetPage();
                  }}
                />
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="content-table up-empty-wrap">
                <Empty
                  type="empty"
                  description={'Nenhum usuário encontrado.\nAjuste a busca ou os filtros e tente novamente.'}
                  showButton={hasFilters}
                  buttonLabel="Limpar filtros"
                  onRetry={() => {
                    setSearch('');
                    setStatusFilter('todos');
                    setRoleFilter('todos');
                    resetPage();
                  }}
                />
              </div>
            ) : (
              <div className="up-table">
                <Table<User>
                  data={pagedData}
                  columns={columns}
                  rowKey={(r) => r.id}
                  size="sm"
                  onRowClick={openView}
                  pagination={{
                    currentPage: page,
                    totalPages,
                    totalItems: filtered.length,
                    pageSize: PAGE_SIZE,
                    onPageChange: setPage,
                  }}
                />
              </div>
            )}
          </div>
        </main>
      </PortalPayfacsLayout>

      <DrawerUsuario
        isOpen={drawerOpen}
        mode={drawerMode}
        user={selectedUser}
        onClose={() => setDrawerOpen(false)}
        onCreate={handleCreate}
        onSave={handleSave}
        onStatusAction={handleStatusAction}
      />

      <ModalMatrizRoles isOpen={matrizOpen} onClose={() => setMatrizOpen(false)} users={users} />

      <Modal
        isOpen={confirmKind !== null}
        title={confirmCopy.title}
        onClose={closeConfirm}
        size="sm"
        footer={{
          primaryButton: { children: confirmCopy.primary, onClick: handleConfirm },
          secondaryButton: { children: 'Cancelar', onClick: closeConfirm },
        }}
      >
        <Text variant="body-sm">{confirmCopy.description}</Text>
      </Modal>
    </>
  );
}
